import type { CoordinatorLabTourSlug } from '../../shared/onboarding/coordinatorLearningLabTours';
import { getLearningLabPlaybook } from '../../shared/onboarding/learningLabPlaybooks';
import {
  loadMissionSession,
  resolveMissionTourStepIndex,
} from '../../shared/onboarding/missionSessionStorage';
import {
  buildMissionNavigatePath,
  isMissionRouteSatisfied,
  missionStepRouteContractFromTourMetadata,
  resolveMissionRouteContract,
} from '../../shared/onboarding/missionRouteContract';
import type { MissionStep } from '../../shared/onboarding/missionTypes';
import { TOUR_DOM_EVENTS } from '../components/onboarding/onboardingDomEvents';
import { waitForTarget } from '../components/onboarding/engine/waitForTarget';
import type { TourStep } from '../types/onboarding';

const TOUR_NAVIGATE_EVENT = TOUR_DOM_EVENTS.NAVIGATE;
const DASHBOARD_READY_SELECTOR =
  '[data-tour="coordinator-tab-events"], [data-tour-stable="coordinator-tab-strip"]';

function syncMissionTabsFromContract(
  contract: ReturnType<typeof resolveMissionRouteContract>,
): void {
  if (contract.embedTab) {
    window.dispatchEvent(
      new CustomEvent(TOUR_DOM_EVENTS.SET_EMBED_TAB, {
        detail: { tab: contract.embedTab },
      }),
    );
  }
  if (contract.certificateTab && contract.certificateTab !== 'overview') {
    window.dispatchEvent(
      new CustomEvent(TOUR_DOM_EVENTS.SET_CERTIFICATE_TAB, {
        detail: { tab: contract.certificateTab },
      }),
    );
  }
}

async function waitForMissionRoute(
  contract: ReturnType<typeof resolveMissionRouteContract>,
): Promise<void> {
  await new Promise((resolve) => window.setTimeout(resolve, 400));

  if (contract.surface === 'dashboard') {
    await waitForTarget(DASHBOARD_READY_SELECTOR, { timeoutMs: 10000 });
  }

  if (contract.surface === 'eventForm') {
    await waitForTarget(
      '[data-tour="event-form-stepper"], [data-tour-stable="event-form-stepper"]',
      { timeoutMs: 10000 },
    );
  }

  let attempts = 0;
  while (!isMissionRouteSatisfied(contract) && attempts < 15) {
    await new Promise((resolve) => window.setTimeout(resolve, 200));
    attempts += 1;
  }
}

function tourStepToMissionStep(step: TourStep, slug: CoordinatorLabTourSlug): MissionStep | null {
  const metadata = step.metadata ?? {};
  if (!metadata.missionStepId) {
    return null;
  }
  return {
    id: String(metadata.missionStepId),
    stepName: step.stepName ?? String(metadata.missionStepId),
    kind: (metadata.missionKind as MissionStep['kind']) ?? 'spotlight',
    groupId: String(metadata.missionGroupId ?? 'start'),
    title: String(step.title ?? ''),
    instruction: String(metadata.instruction ?? step.content ?? ''),
    advanceGate: metadata.advanceGate as MissionStep['advanceGate'],
    tourAnchor:
      typeof metadata.tourAnchor === 'string' ? metadata.tourAnchor : undefined,
    coordinatorTabSlug:
      typeof metadata.coordinatorTabSlug === 'string'
        ? metadata.coordinatorTabSlug
        : undefined,
    module: metadata.module as MissionStep['module'],
    placement: step.placement,
    metadata,
    isPanelTarget: metadata.isPanelTarget === true,
  };
}

export function routeContractForTourStep(
  step: TourStep,
  slug: CoordinatorLabTourSlug,
): ReturnType<typeof resolveMissionRouteContract> | null {
  const missionStep = tourStepToMissionStep(step, slug);
  if (missionStep) {
    return resolveMissionRouteContract(missionStep, slug);
  }
  return missionStepRouteContractFromTourMetadata(step.metadata, slug);
}

export async function ensureMissionRouteForTourStep(
  step: TourStep,
  slug: CoordinatorLabTourSlug,
): Promise<void> {
  const contract = routeContractForTourStep(step, slug);
  if (!contract) {
    return;
  }

  if (isMissionRouteSatisfied(contract)) {
    return;
  }

  const path = buildMissionNavigatePath(contract);
  if (!path) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TOUR_NAVIGATE_EVENT, {
      detail: { path, replace: true },
    }),
  );

  syncMissionTabsFromContract(contract);
  await waitForMissionRoute(contract);
}

export async function ensureMissionRouteForPlaybookStep(
  step: MissionStep,
  slug: CoordinatorLabTourSlug,
): Promise<void> {
  const contract = resolveMissionRouteContract(step, slug);
  if (isMissionRouteSatisfied(contract)) {
    return;
  }
  const path = buildMissionNavigatePath(contract);
  if (!path) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TOUR_NAVIGATE_EVENT, {
      detail: { path, replace: true },
    }),
  );

  syncMissionTabsFromContract(contract);
  await waitForMissionRoute(contract);
}

/** Ensure route for a resumed mission before spotlight/tour engine starts. */
export async function ensureMissionRouteForResumedMission(
  userId: number,
  slug: CoordinatorLabTourSlug,
  tourSteps: ReadonlyArray<TourStep>,
): Promise<void> {
  const snapshot = loadMissionSession(userId, slug);
  if (!snapshot) {
    return;
  }
  const playbook = getLearningLabPlaybook(slug);
  if (!playbook) {
    return;
  }
  const stepIndex = resolveMissionTourStepIndex(tourSteps, snapshot);
  const tourStep = tourSteps[stepIndex];
  if (tourStep) {
    await ensureMissionRouteForTourStep(tourStep, slug);
    return;
  }
  const playbookStep = playbook.steps[stepIndex];
  if (playbookStep) {
    await ensureMissionRouteForPlaybookStep(playbookStep, slug);
  }
}
